/** Shared colors for the ordered participants in a council. */
export interface ExpertPalette {
  border: string;
  headerBg: string;
  accentText: string;
  badgeBg: string;
  badgeText: string;
  bar: string;
  ring: string;
  activeBg: string;
  hex: string;
  rgb: [number, number, number];
}

export interface StepStyle {
  label: string;
  emoji: string;
  headerBg: string;
  headerText: string;
  contentBg: string;
  border: string;
}

function palette(colors: Omit<ExpertPalette, "rgb">): ExpertPalette {
  const rgb = colors.hex.match(/[a-f0-9]{2}/gi)!.map(channel => parseInt(channel, 16)) as [number, number, number];
  return { ...colors, rgb };
}

// Teal and navy come from the app's core palette; the remaining muted tones
// distinguish participants without competing with status and action colors.
export const EXPERT_PALETTES: readonly ExpertPalette[] = [
  palette({
    hex: "#447580",
    border: "border-expert-teal-line", headerBg: "bg-expert-teal-soft",
    accentText: "text-expert-teal-text", badgeBg: "bg-expert-teal-badge",
    badgeText: "text-expert-teal-text", bar: "bg-expert-teal-bar",
    ring: "ring-expert-teal-line", activeBg: "bg-expert-teal-active",
  }),
  palette({
    hex: "#9b5149",
    border: "border-expert-clay-line", headerBg: "bg-expert-clay-soft",
    accentText: "text-expert-clay-text", badgeBg: "bg-expert-clay-badge",
    badgeText: "text-expert-clay-text", bar: "bg-expert-clay-bar",
    ring: "ring-expert-clay-line", activeBg: "bg-expert-clay-active",
  }),
  palette({
    hex: "#344265",
    border: "border-expert-navy-line", headerBg: "bg-expert-navy-soft",
    accentText: "text-expert-navy-text", badgeBg: "bg-expert-navy-badge",
    badgeText: "text-expert-navy-text", bar: "bg-expert-navy-bar",
    ring: "ring-expert-navy-line", activeBg: "bg-expert-navy-active",
  }),
  palette({
    hex: "#8a672b",
    border: "border-expert-ochre-line", headerBg: "bg-expert-ochre-soft",
    accentText: "text-expert-ochre-text", badgeBg: "bg-expert-ochre-badge",
    badgeText: "text-expert-ochre-text", bar: "bg-expert-ochre-bar",
    ring: "ring-expert-ochre-line", activeBg: "bg-expert-ochre-active",
  }),
  palette({
    hex: "#567047",
    border: "border-expert-olive-line", headerBg: "bg-expert-olive-soft",
    accentText: "text-expert-olive-text", badgeBg: "bg-expert-olive-badge",
    badgeText: "text-expert-olive-text", bar: "bg-expert-olive-bar",
    ring: "ring-expert-olive-line", activeBg: "bg-expert-olive-active",
  }),
];

const NEUTRAL_PALETTE = palette({
  hex: "#5a6e7a", border: "border-line", headerBg: "bg-subtle",
  accentText: "text-content", badgeBg: "bg-raised", badgeText: "text-muted",
  bar: "bg-control", ring: "ring-control", activeBg: "bg-pressed",
});

/** Selection order is saved with a debate, keeping UI, audio and export aligned. */
export function getExpertPalette(expert: string, selectedExperts: readonly string[]): ExpertPalette {
  const lineup = Array.from(new Set(selectedExperts.map(id => id.toLowerCase())));
  const index = lineup.indexOf(expert.toLowerCase());
  return EXPERT_PALETTES[index] ?? NEUTRAL_PALETTE;
}

export function getExpertColor(expert: string, selectedExperts: readonly string[]): string {
  return getExpertPalette(expert, selectedExperts).border;
}

/** Styling for each debate step type (initial, feedback, revision) */
export function getStepStyle(stepType: string): StepStyle {
  switch (stepType) {
    case "frame":
      return {
        label: "Framing",
        emoji: "🎯",
        headerBg: "bg-accent-soft",
        headerText: "text-accent",
        contentBg: "bg-accent-soft/30",
        border: "border-accent-line",
      };
    case "research_gather":
      return {
        label: "Web Research",
        emoji: "🔍",
        headerBg: "bg-accent-soft",
        headerText: "text-accent",
        contentBg: "bg-accent-soft/30",
        border: "border-accent-line",
      };
    case "initial":
      return {
        label: "Initial Response",
        emoji: "💬",
        headerBg: "bg-accent-soft",
        headerText: "text-accent",
        contentBg: "bg-surface",
        border: "border-accent-line",
      };
    case "feedback":
      return {
        label: "Peer Feedback",
        emoji: "🔍",
        headerBg: "bg-warning-soft",
        headerText: "text-warning",
        contentBg: "bg-warning-soft/30",
        border: "border-warning-line",
      };
    case "revision":
      return {
        label: "Revised Response",
        emoji: "✏️",
        headerBg: "bg-success-soft",
        headerText: "text-success",
        contentBg: "bg-success-soft/30",
        border: "border-success-line",
      };
    case "fact_check":
      return {
        label: "Fact Check",
        emoji: "✅",
        headerBg: "bg-success-soft",
        headerText: "text-success",
        contentBg: "bg-success-soft/30",
        border: "border-success-line",
      };
    default:
      return {
        label: stepType,
        emoji: "📝",
        headerBg: "bg-subtle",
        headerText: "text-content",
        contentBg: "bg-surface",
        border: "border-line",
      };
  }
}

/** Helper to get initials from display name */
export function getInitials(name?: string): string {
  if (!name) return "U";
  return name
    .split(" ")
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
}
